import { useDispatch } from "react-redux";
import {
  userLoginRequest,
  userLoginSuccess,
  userLogoutRequest,
  setAccessLevel,
  setProfileNotFound,
  setDarkMode
} from "../store/actions/graphqlActions";

import keycloak from "../keycloak";
import { useEffect } from "react";
import { useLazyQuery } from "@apollo/client";
import { GET_MY_PROFILE } from "../services/graphql"; 

const useAuth = () => {
  const dispatch = useDispatch();
  const dashboardRedirectUri = typeof window !== "undefined" ? `${window.location.origin}/` : undefined;

  const [fetchProfile, { data: profileData, error: profileError }] = useLazyQuery(GET_MY_PROFILE);

  // Persist auth state
  useEffect(() => {
    if (keycloak.authenticated) {
      const token = keycloak.tokenParsed;

      dispatch(userLoginSuccess({ email: token.email }));

      // Fetch accessLevel from DB
      fetchProfile({ variables: { email: token.email } });
    }
  }, [dispatch, fetchProfile]);

  const retryProfileFetch = () => {
    const token = keycloak.tokenParsed;
    if (token?.email) {
      fetchProfile({ variables: { email: token.email } });
    }
  };

  useEffect(() => {
    if (!profileData) return; // query hasn't come back yet — stay in the loading state
    const body = profileData?.getUserProfile?.body;
    const level = body?.accessLevel;
    if (level) {
      dispatch(setAccessLevel(level));
    } else {
      // Query came back, but there's no matching profile (or no accessLevel on it) —
      // this is a definitive "not found", not "still loading".
      dispatch(setProfileNotFound());
    }

    // Reconcile the theme guessed from localStorage/system preference with the
    // user's saved preference once we actually know it.
    if (body && body.darkMode !== undefined && body.darkMode !== null) {
      dispatch(setDarkMode(body.darkMode === true || body.darkMode === 'true'));
    }
  }, [profileData, dispatch]);

  useEffect(() => {
    if (profileError) {
      console.error("Profile fetch error:", profileError);
    }
  }, [profileError]);

  // Token auto-refresh
  useEffect(() => {
    if (!keycloak) return;

    const interval = setInterval(() => {
      keycloak.updateToken(30).catch(() => {
        keycloak.login({ redirectUri: dashboardRedirectUri });
      });
    }, 20000);

    return () => clearInterval(interval);
  }, []);

  const userLogin = () => {
    dispatch(userLoginRequest());
    keycloak.login({ redirectUri: dashboardRedirectUri });
  };

  const userLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    dispatch(userLogoutRequest());
    keycloak.logout({ redirectUri: dashboardRedirectUri });
  };

  return {
    authenticated: keycloak.authenticated,
    userLogout,
    userLogin,
    profileError,
    retryProfileFetch,
  };
};

export default useAuth;
